import { useState, useMemo } from "react";

export interface Supervisor {
  id: number;
  aia_supervisor_id: string;
  first_name: string;
  last_name: string;
  email: string;
  enterpriseId: number;
  enterprise?: {
    nom: string;
    country: string;
    language: string;
  };
}

export interface SupervisorFilters {
  search: string;
  enterprise: string;
  country: string;
  language: string;
  sortBy: "name" | "email" | "enterprise" | "country" | "";
  sortOrder: "asc" | "desc";
}

export const useSupervisorFilters = (
  supervisors: Supervisor[],
  initialFilters?: Partial<SupervisorFilters>,
) => {
  const [filters, setFilters] = useState<SupervisorFilters>({
    search: "",
    enterprise: "",
    country: "",
    language: "",
    sortBy: "",
    sortOrder: "asc",
    ...initialFilters,
  });

  // Generate filter options from the data
  // Filter and sort the supervisors
  /**
   * Only sorts. `search`, `enterprise`, `country` and `language` are sent to the
   * API by the page, which filters the whole table — matching them again here
   * would be redundant, and matching them *only* here would restrict the result
   * to whichever rows the current page happens to hold.
   */
  const filteredSupervisors = useMemo(() => {
    let filtered = [...supervisors];

    // Sort the results
    if (filters.sortBy) {
      filtered.sort((a, b) => {
        let aValue: string = "";
        let bValue: string = "";

        switch (filters.sortBy) {
          case "name":
            aValue = `${a.first_name} ${a.last_name}`;
            bValue = `${b.first_name} ${b.last_name}`;
            break;
          case "email":
            aValue = a.email;
            bValue = b.email;
            break;
          case "enterprise":
            aValue = a.enterprise?.nom || "";
            bValue = b.enterprise?.nom || "";
            break;
          case "country":
            aValue = a.enterprise?.country || "";
            bValue = b.enterprise?.country || "";
            break;
        }

        const comparison = aValue.localeCompare(bValue);
        return filters.sortOrder === "desc" ? -comparison : comparison;
      });
    }

    return filtered;
  }, [supervisors, filters]);

  const updateFilter = (key: keyof SupervisorFilters, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const updateSort = (sortBy: SupervisorFilters["sortBy"]) => {
    setFilters((prev) => ({
      ...prev,
      sortBy,
      sortOrder:
        prev.sortBy === sortBy && prev.sortOrder === "asc" ? "desc" : "asc",
    }));
  };

  const resetFilters = () => {
    setFilters({
      search: "",
      enterprise: "",
      country: "",
      language: "",
      sortBy: "",
      sortOrder: "asc",
    });
  };

  const hasActiveFilters = useMemo(() => {
    return (
      filters.search ||
      filters.enterprise ||
      filters.country ||
      filters.language
    );
  }, [filters]);

  return {
    filters,
    filteredSupervisors,
    updateFilter,
    updateSort,
    resetFilters,
    hasActiveFilters,
  };
};
